import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
import { enterpriseGroupResponse, enterpriseOrgResponse } from "~/backend/validate.js";
/** @import { scimGroupResponse, scimOrgMappingResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest, paginatedResponse, query, response } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
/** @import { Placement } from "~/util/float.js"; */
import html from "~/util/html.js";
import { popover } from "~/util/popover.js";
import Query from "~/util/query.js";
import { array } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Icon from "~/component/Icon.js";
import Combobox from "~/admin/component/Combobox.js";
/** @import { Option } from "~/admin/component/Combobox.js"; */

import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./manageMappings.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./manageMappings.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * What a mapping needs to be saved; `scimOrgMappingResponse` satisfies it.
 *
 * @typedef {{ org_id: string; role: string; groups: { group_id: string }[] }} Mapping
 */

/**
 * Replaces a SCIM group's org mappings.
 *
 * @param {{ enterpriseId: string }} props
 * @param {scimGroupResponse} group
 * @param {Mapping[]} mappings
 * @returns {Promise<true | undefined>} True once saved, undefined on failure
 */
export const saveMappings = async ({
  enterpriseId
}, group, mappings) => {
  try {
    await authedApiRequest("PUT", `/v2/enterprises/${enterpriseId}/scim-groups/${group.id}/mappings`, {
      json: {
        mappings: mappings.map(mapping => ({
          org_id: mapping.org_id,
          role: mapping.role,
          group_ids: mapping.groups.map(g => g.group_id)
        }))
      }
    });
    Query.invalidate();
    return true;
  } catch (err) {
    alert(ApiError.from(err).message, {
      type: "error"
    });
    return;
  }
};

/**
 * @param {{ anchor: HTMLButtonElement; enterpriseId: string; placement: Placement }} props
 * @param {scimGroupResponse} group
 */
export const manageMappings = async ({
  anchor,
  enterpriseId,
  placement
}, group) => {
  const mappings = await popover({
    anchor,
    placement,
    content: (/** @type {(result?: Mapping[]) => void} */resolve) => h(PopoverChildren, {
      resolve,
      enterpriseId,
      mappings: group.mappings
    })
  });
  if (mappings === undefined) return;
  return saveMappings({
    enterpriseId
  }, group, mappings);
};

/**
 * Group options are keyed `${org_id}/${group_id}` so a selection carries its org.
 *
 * @extends {Component<PopoverChildrenProps>}
 *
 * @typedef {object} PopoverChildrenProps
 * @property {string} enterpriseId
 * @property {scimOrgMappingResponse[]} mappings
 * @property {(result?: Mapping[]) => void} resolve
 */
class PopoverChildren extends Component {
  query = signal("");
  initial = (/** @type {Map<string, Option>} */
  /** @type {Map<string, Option>} */
  new Map(this.props.mappings.flatMap(mapping => [[mapping.org_id, {
    id: mapping.org_id,
    label: mapping.org_name
  }], ...mapping.groups.map(group => {
    const id = `${mapping.org_id}/${group.group_id}`;
    return /** @type {const} */[id, {
      id,
      label: `${group.group_name} — ${mapping.org_name}`
    }];
  })])));
  selected = signal(this.initial);
  orgs = query(() => ({
    key: [`/v2/enterprises/${this.props.enterpriseId}/orgs`, {
      limit: 9999
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(enterpriseOrgResponse)
  });
  groups = query(() => ({
    key: [`/v2/enterprises/${this.props.enterpriseId}/groups`, {
      limit: 5,
      q: this.query.value
    }],
    keepPreviousData: true
  }), {
    schema: response(array(enterpriseGroupResponse))
  });
  submit() {
    const roles = new Map(this.props.mappings.map(mapping => [mapping.org_id, mapping.role]));

    /** @type {Map<string, Mapping>} */
    const mappings = new Map();
    for (const id of this.selected.value.keys()) {
      const [orgId = id, groupId] = id.split("/");
      let mapping = mappings.get(orgId);
      if (!mapping) {
        mapping = {
          org_id: orgId,
          role: roles.get(orgId) ?? "member",
          groups: []
        };
        mappings.set(orgId, mapping);
      }
      if (groupId) mapping.groups.push({
        group_id: groupId
      });
    }
    this.props.resolve([...mappings.values()]);
  }
  render() {
    const search = this.query.value.toLowerCase();
    const selected = this.selected.value;
    const unchanged = selected.size === this.initial.size && [...this.initial.keys()].every(id => selected.has(id));
    return __h("div", {
      "class": css.popover
    }, h(Combobox, {
      query: this.query,
      debounce: 200,
      loading: this.orgs.loading || this.groups.loading,
      multiSelected: this.selected,
      options: [...(this.orgs.data?.data.filter(org => org.name.toLowerCase().includes(search)).slice(0, 5).map(org => ({
        id: org.id,
        label: org.name,
        render: () => __h("div", {
          "class": css.target
        }, __h(Icon, {
          name: "office",
          size: 14
        }), __h("div", {
          "class": css.text + " " + util.truncate
        }, org.name))
      })) ?? []), ...(this.groups.data?.data.map(group => ({
        id: `${group.org_id}/${group.id}`,
        label: `${group.name} — ${group.org_name}`,
        render: () => __h("div", {
          "class": css.target
        }, __h(Icon, {
          name: "users",
          size: 14
        }), __h("div", {
          "class": css.text + " " + util.truncate
        }, group.name), __h("div", {
          "class": css.sub
        }, group.org_name))
      })) ?? [])]
    }), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      level: "primary",
      disabled: unchanged,
      onClick: () => this.submit()
    }, "Update mappings"), __h(Button, {
      onClick: () => this.props.resolve()
    }, "Cancel")));
  }
}